import React, { useState } from 'react';
import { HelpCircle } from 'react-feather';
import classnames from 'classnames';
import Popup from 'components/quashed/Popup';
import Button from 'components/quashed/Button';
import Input, {
  INPUT_FORM_FIELD_V3_TYPES,
} from 'components/form-fields-v3/Input';
import { trackEventWithData, TRACKING_EVENTS } from 'helpers/segment';
import styles from './styles/tooltipPopup.scss';

interface ITooltipPopupProps {
  className?: string;
  toggleClassName?: string;
  toggleContainerClassName?: string;
  triggerSize?: number;
  title?: string;
  numberPlateLabel?: string;
  yearLabel?: string;
  makeLabel?: string;
  modelLabel?: string;
  buttonText?: string;
  successMessage?: string;
  eventName?: string;
  onSubmit?: (data: {
    numberPlate: string;
    year: string;
    make: string;
    model: string;
  }) => void;
}

const TooltipPopup = ({
  className,
  toggleClassName,
  toggleContainerClassName,
  triggerSize = 16,
  title = "Can't find your vehicle on the list?",
  numberPlateLabel = 'Number Plate',
  yearLabel = 'Year',
  makeLabel = 'Make',
  modelLabel = 'Model & Sub-Model',
  buttonText = 'Request',
  successMessage = 'Thank you for letting us know, we will work on this.',
  eventName = TRACKING_EVENTS.MarketScanVehicleRequestMakeModel,
  onSubmit,
}: ITooltipPopupProps) => {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [numberPlate, setNumberPlate] = useState('');
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');

  const handleOpen = () => {
    setOpen(true);
    setSubmitted(false);
  };

  const handleClose = () => {
    setOpen(false);
    // Reset form after a delay to avoid visual glitch
    setTimeout(() => {
      setSubmitted(false);
      setNumberPlate('');
      setYear('');
      setMake('');
      setModel('');
    }, 300);
  };

  const handleNumberPlateChange = (value: string | number) => {
    // Convert to uppercase and limit to 6 characters
    const upperCased = String(value).toUpperCase().slice(0, 6);
    setNumberPlate(upperCased);
  };

  const handleYearChange = (value: string | number) => {
    // Only allow numbers and limit to 4 characters
    const numericValue = String(value).replace(/\D/g, '').slice(0, 4);
    setYear(numericValue);
  };

  const handleMakeChange = (value: string | number) => {
    // Convert to uppercase and limit to 100 characters
    const upperCased = String(value).toUpperCase().slice(0, 100);
    setMake(upperCased);
  };

  const handleModelChange = (value: string | number) => {
    // Convert to uppercase and limit to 100 characters
    const upperCased = String(value).toUpperCase().slice(0, 100);
    setModel(upperCased);
  };

  const handleSubmit = () => {
    const data = {
      numberPlate,
      year,
      make,
      model,
    };

    // Track the event in Segment
    trackEventWithData(eventName, data);

    // Call optional onSubmit callback
    if (onSubmit) {
      onSubmit(data);
    }

    setSubmitted(true);
  };

  const isFormValid =
    numberPlate.trim() && year.trim() && make.trim() && model.trim();

  return (
    <>
      <div
        className={classnames(styles.toggleContainer, toggleContainerClassName)}
      >
        <HelpCircle
          size={triggerSize}
          className={classnames(styles.toggle, toggleClassName)}
          onClick={handleOpen}
        />
      </div>
      <Popup
        className={classnames(styles.popup, className)}
        open={open}
        onClose={handleClose}
        showCloseIcon
        closeOnDimmerClick
      >
        <div className={styles.content}>
          {!submitted ? (
            <>
              <h2 className={styles.title}>{title}</h2>
              <div className={styles.form}>
                <div style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      fontWeight: '700',
                      fontSize: '14px',
                      color: '#4A4D59',
                      marginBottom: '4px',
                    }}
                  >
                    {numberPlateLabel}
                  </div>
                  <Input
                    type={INPUT_FORM_FIELD_V3_TYPES.TEXT}
                    value={numberPlate}
                    onChange={handleNumberPlateChange}
                    placeholder="e.g., ABC123"
                    maxLength={6}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      fontWeight: '700',
                      fontSize: '14px',
                      color: '#4A4D59',
                      marginBottom: '4px',
                    }}
                  >
                    {yearLabel}
                  </div>
                  <Input
                    type={INPUT_FORM_FIELD_V3_TYPES.TEXT}
                    value={year}
                    onChange={handleYearChange}
                    placeholder="e.g., 2020"
                    maxLength={4}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      fontWeight: '700',
                      fontSize: '14px',
                      color: '#4A4D59',
                      marginBottom: '4px',
                    }}
                  >
                    {makeLabel}
                  </div>
                  <Input
                    type={INPUT_FORM_FIELD_V3_TYPES.TEXT}
                    value={make}
                    onChange={handleMakeChange}
                    placeholder="e.g., Toyota"
                    maxLength={100}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      fontWeight: '700',
                      fontSize: '14px',
                      color: '#4A4D59',
                      marginBottom: '4px',
                    }}
                  >
                    {modelLabel}
                  </div>
                  <Input
                    type={INPUT_FORM_FIELD_V3_TYPES.TEXT}
                    value={model}
                    onChange={handleModelChange}
                    placeholder="e.g., Camry Hybrid"
                    maxLength={100}
                  />
                </div>
                <Button
                  text={buttonText}
                  onClick={handleSubmit}
                  disabled={!isFormValid}
                  className={styles.button}
                />
              </div>
            </>
          ) : (
            <div className={styles.success}>
              <p className={styles.successMessage}>{successMessage}</p>
            </div>
          )}
        </div>
      </Popup>
    </>
  );
};

export default TooltipPopup;
